/**
 * One catalog entity, as a card to pick like a product: icon, name, what it's for, its fields, its toppings as chips
 * (other countries' or regions' in a menu after them), and Add, into the basket.
 */
import type { TemplateOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { flagged, otherFacetsByGeography, type Pick } from "../../lib/catalog.ts";
import { Chip } from "../../ui/Chip.tsx";
import { EntityGlyph } from "../../ui/EntityGlyph.tsx";
import { AddToBasketIcon, CheckIcon, InBasketIcon, PlusIcon } from "../../ui/icons.tsx";
import { Menu } from "../../ui/Menu.tsx";
import { FOCUS } from "../../ui/styles.ts";

/** How many field names the card lists before "+ n more". */
const PREVIEW = 6;

/** The other countries' or regions' menu's trigger: lighter than the toppings' chips (it's a way to more, not one). */
const MORE_CHIP = `inline-flex h-6 shrink-0 items-center rounded-full border border-dashed border-zinc-300 px-2.5 text-xs whitespace-nowrap text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-800 ${FOCUS}`;

export function EntityCard(p: {
  entity: TemplateOut;
  pick: Pick | undefined;
  /** Its geography's name, for a country's or region's own entities. */
  geography: string | undefined;
  /** Names of the geographies, by key (for other countries' or regions' toppings). */
  geoName: (key: string) => string;
  /** The category's hub: the entity one whole operation is. */
  hub?: boolean;
  onToggle: () => void;
  onFacet: (facet: string) => void;
}) {
  const e = p.entity;
  const on = p.pick !== undefined;
  const names = e.fields.map((f) => f.label ?? f.key);
  const more = names.length - PREVIEW;
  const chosen = (key: string) => p.pick?.facets.includes(key) ?? false;
  const elsewhere = otherFacetsByGeography(e);
  // Other countries' or regions' toppings once chosen join the chips (to take them off again). A country's or region's
  // own (the chosen one's too) shows its flag; those for everywhere don't.
  const chips = [
    ...e.facets.map((f) => ({ f, geography: f.geography ?? undefined })),
    ...elsewhere.flatMap((g) => g.facets.filter((f) => chosen(f.key)).map((f) => ({ f, geography: g.geography }))),
  ];
  return (
    <article
      class={`flex flex-col gap-3 rounded-lg border bg-white p-4 transition-colors ${on ? "border-blue-500 ring-1 ring-blue-500" : "border-zinc-200 hover:border-zinc-300"}`}
    >
      <div class="flex items-start gap-3">
        <EntityGlyph icon={e.icon} size={20} pad={6} />
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 class="font-medium text-zinc-900">{e.name}</h3>
            {p.hub ? <Chip tone="green">{t("catalog.hub")}</Chip> : null}
            {p.geography && e.geography ? (
              <Chip tone="blue" title={t("catalog.regionBadge", { name: p.geography })}>
                {flagged(e.geography, p.geography)}
              </Chip>
            ) : null}
          </div>
          {p.hub ? <p class="mt-0.5 text-xs text-green-700">{t("catalog.hubHint")}</p> : null}
          <p class="mt-0.5 text-sm text-zinc-600">{e.description}</p>
        </div>
      </div>
      <p class="text-xs text-zinc-500">
        {names.slice(0, PREVIEW).join(" · ")}
        {more > 0 ? ` · ${t("catalog.moreFields", { count: more })}` : ""}
      </p>
      {chips.length > 0 || elsewhere.length > 0 ? (
        <ul class="flex flex-wrap gap-1.5" aria-label={t("catalog.toppings", { name: e.name })}>
          {chips.map(({ f, geography }) => {
            const c = chosen(f.key);
            return (
              <li key={f.key}>
                <Chip
                  size="md"
                  tone={c ? "blue" : "gray"}
                  pressed={c}
                  title={f.description}
                  onClick={() => p.onFacet(f.key)}
                  icon={c ? <CheckIcon size={12} /> : <PlusIcon size={12} />}
                >
                  {geography === undefined ? f.name : flagged(geography, f.name)}
                </Chip>
              </li>
            );
          })}
          {elsewhere.length > 0 ? (
            <li>
              <Menu
                class={MORE_CHIP}
                label={
                  <>
                    <span aria-hidden="true">{t("catalog.otherRegions")}</span>
                    <span class="sr-only">{t("catalog.otherRegionsFor", { name: e.name })}</span>
                  </>
                }
                items={elsewhere.flatMap((g) =>
                  g.facets.map((f) => ({
                    key: f.key,
                    label: f.name,
                    detail: flagged(g.geography, p.geoName(g.geography)),
                    current: chosen(f.key),
                    onSelect: () => p.onFacet(f.key),
                  })),
                )}
              />
            </li>
          ) : null}
        </ul>
      ) : null}
      {/* Like a product's: into the basket, at the foot of the card. */}
      <div class="mt-auto flex justify-end">
        <button
          type="button"
          aria-pressed={on}
          aria-label={t("catalog.add", { name: e.name })}
          onClick={p.onToggle}
          class={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors ${FOCUS} ${on ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700" : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"}`}
        >
          {on ? <InBasketIcon /> : <AddToBasketIcon />}
          {t(on ? "catalog.addedShort" : "catalog.addShort")}
        </button>
      </div>
    </article>
  );
}
